import api from '../api/nodeBackApi';
import { getObraByFeedID } from '../obras/api/obras.api';
import { getObraTimeZone } from '../videoEditor/utils/obraTimeZone';

// Fotos de la cámara: mismo endpoint que usa la Galería (webback, S3 presignado).
const WEBBACK_URL = process.env.REACT_APP_WEBBACK_URL || 'https://webback.lisual.com';

const authConfig = () => {
  const token = localStorage.getItem('authLisualToken');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
};

export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// En desarrollo local el bucket no acepta localhost por CORS: las fotos pasan por /s3img (src/setupProxy.js)
// para que el canvas pueda exportar. En producción se usa la URL directa (requiere pro.lisual.com en el CORS del bucket).
export const imgUrl = (url) => {
  if (process.env.NODE_ENV !== 'development') return url;
  const m = /^https:\/\/([a-z0-9.-]+\.amazonaws\.com)\/(.*)$/.exec(url || '');
  return m ? `/s3img/${m[1]}/${m[2]}` : url;
};

export async function daily(feedID, date) {
  try {
    const { data } = await api.get(`${WEBBACK_URL}/api/s3-images/${feedID}/daily?date=${ymd(date)}`, { ...authConfig(), timeout: 20000 });
    return (Array.isArray(data) ? data : [])
      .map((it) => ({ url: it.url || it, ts: new Date(it.date || it.lastModified || it.timestamp || 0) }))
      .filter((it) => it.url && !isNaN(it.ts));
  } catch (e) {
    return [];
  }
}

// Zone of the camera's obra; null when unknown (callers then keep the Argentina default).
export const getFeedTimeZone = (feedID) => (feedID ? getObraByFeedID(feedID).then(getObraTimeZone).catch(() => null) : Promise.resolve(null));

const zoneParts = (d, tz) => Object.fromEntries(
  new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric' })
    .formatToParts(d).map((p) => [p.type, p.value])
);
// Hour of day (decimal) of an instant: in `tz` when given, otherwise Argentina (UTC-3).
export const hourAR = (d, tz) => {
  if (!tz) return (d.getUTCHours() + 21) % 24 + d.getUTCMinutes() / 60;
  const p = zoneParts(d, tz);
  return Number(p.hour) + Number(p.minute) / 60;
};
// Calendar day (YYYY-MM-DD) of an instant: in `tz` when given, otherwise Argentina (UTC-3).
export const dayKeyAR = (d, tz) => {
  if (!tz) return ymd(new Date(d.getTime() - 3 * 3600e3 + d.getTimezoneOffset() * 60e3));
  const p = zoneParts(d, tz);
  return `${p.year}-${p.month}-${p.day}`;
};
const nearest = (list, h, tz) => list.reduce((best, it) => (!best || Math.abs(hourAR(it.ts, tz) - h) < Math.abs(hourAR(best.ts, tz) - h) ? it : best), null);

// corre tareas con un tope de pedidos simultáneos
export async function pool(items, n, fn) {
  const out = new Array(items.length); let i = 0;
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, async () => {
    while (i < items.length) { const k = i++; out[k] = await fn(items[k], k); }
  }));
  return out;
}

// fecha de alta: el customerId termina en ddmmaaaa (ej. gsg11022026)
function altaDesde(customerId) {
  const m = /(\d{2})(\d{2})(\d{4})$/.exec(customerId || '');
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return isNaN(d) || d > new Date() ? null : d;
}

/**
 * Arma el material de la obra: ~16 fotos repartidas desde el alta de la cámara + las de hoy hora por hora.
 * Todos los pedidos van en paralelo (de a 6) y cada uno corta a los 20 s, así la pantalla nunca queda colgada.
 */
export async function loadObraPhotos(feedID, customerId, onProgress, N = 16) {
  const tz = await getFeedTimeZone(feedID);
  const today = new Date();
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const [tl, yl] = await Promise.all([daily(feedID, today), daily(feedID, yest)]);
  // de noche o temprano todavía no hay fotos de hoy: se usa ayer
  const useToday = tl.length >= 6 || tl.length >= yl.length;
  const hoyList = useToday ? tl : yl, hoyDate = useToday ? today : yest;
  const hoy = [];
  for (let h = 8; h <= 18; h++) {
    const it = hoyList.find((x) => Math.floor(hourAR(x.ts, tz)) === h);
    if (it) hoy.push({ src: imgUrl(it.url), hora: `${String(h).padStart(2, '0')}:00`, score: 10 - Math.abs(h - 13) });
  }

  const start = altaDesde(customerId) || new Date(today.getTime() - 365 * 864e5);
  const span = Math.max(1, Math.round((hoyDate - start) / 864e5));
  const dates = [];
  for (let k = 0; k < N; k++) { const d = new Date(start); d.setDate(d.getDate() + 3 + Math.round(k * (span - 4) / (N - 1))); if (d < hoyDate) dates.push(d); }
  let done = 0;
  const res = await pool(dates, 6, async (d) => {
    let list = await daily(feedID, d);
    if (!list.length) { const d2 = new Date(d); d2.setDate(d2.getDate() + 2); list = await daily(feedID, d2); if (list.length) d = d2; }
    done++; if (onProgress) onProgress(done, dates.length);
    const it = nearest(list, 13, tz);
    return it ? { src: imgUrl(it.url), fecha: ymd(d), hora: '13:00' } : null;
  });
  const hist = res.filter(Boolean);
  const hero = nearest(hoyList, 13, tz);
  if (hero) hist.push({ src: imgUrl(hero.url), fecha: ymd(hoyDate), hora: '13:00' });
  return { hist, hoy, hoyFecha: ymd(hoyDate) };
}

// ───── IA de obra: personas / casco / vehículos por foto (Firestore pirIaAnalyzes, vía backend) ─────
const toDate = (v) => {
  if (!v) return null;
  if (typeof v === 'object' && (v._seconds || v.seconds)) return new Date((v._seconds || v.seconds) * 1000);
  const d = new Date(v); return isNaN(d) ? null : d;
};
const hhmm = (h) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;

/** Resumen por día de los últimos `days` días. Devuelve null si la cámara no tiene análisis de IA. */
export async function loadIA(identifier, customerId, days = 14, tz = null) {
  if (!identifier || !customerId) return null;
  const end = new Date(), start = new Date(); start.setDate(start.getDate() - (days - 1));
  // el endpoint trabaja por mes: se parte el rango si cruza de mes
  const tramos = [];
  for (let d = new Date(start); d <= end;) {
    const finMes = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const hasta = finMes < end ? finMes : end;
    tramos.push({ startDay: d.getDate(), endDay: hasta.getDate(), month: String(d.getMonth() + 1).padStart(2, '0'), year: d.getFullYear() });
    d = new Date(hasta); d.setDate(d.getDate() + 1);
  }
  let items = [];
  try {
    const res = await Promise.all(tramos.map((t) => api.post(`/api/dataAnalyzed/getWeeklyData/${identifier}/`, { ...t, customerId }, { timeout: 25000 }).then((r) => r.data || []).catch(() => [])));
    items = res.flat();
  } catch (e) { return null; }
  const porDia = {};
  for (const it of items) {
    const dt = toDate(it.date); if (!dt) continue;
    const key = dayKeyAR(dt, tz);
    const h = hourAR(dt, tz);
    const p = Number(it.numberOfPerson) || 0;
    const d = porDia[key] || (porDia[key] = { fecha: key, max: 0, casco: 0, autos: 0, camionetas: 0, excavadoras: 0, horas: new Array(24).fill(0), first: null, last: null });
    if (p > d.max) { d.max = p; d.casco = Math.min(p, Number(it.numberOfPersonWithHelmet) || 0); }
    d.autos = Math.max(d.autos, Number(it.numberOfCars) || 0);
    d.camionetas = Math.max(d.camionetas, Number(it.numberOfPickUps) || 0);
    d.excavadoras = Math.max(d.excavadoras, Number(it.numberOfExcavators) || 0);
    d.horas[Math.floor(h)] = Math.max(d.horas[Math.floor(h)], p);
    if (p > 0) { d.first = d.first == null ? h : Math.min(d.first, h); d.last = d.last == null ? h : Math.max(d.last, h); }
  }
  const dias = Object.values(porDia).sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((d) => ({ ...d, primera: d.first != null ? hhmm(d.first) : null, ultima: d.last != null ? hhmm(d.last) : null }));
  if (!dias.length || !dias.some((d) => d.max > 0)) return null;
  const ultimo = dias.slice().reverse().find((d) => d.max > 0).fecha;
  return { dias, ultimo };
}

export async function loadClima(lat, lng) {
  if (!lat || !lng) return null;
  try {
    const u = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code,wind_speed_10m&timezone=America%2FArgentina%2FBuenos_Aires`;
    const w = (await (await fetch(u)).json()).current, code = w.weather_code;
    const [desc, icon] = code === 0 ? ['Despejado', '☀️'] : code <= 2 ? ['Parcialmente nublado', '⛅'] : code === 3 ? ['Nublado', '☁️'] : code <= 48 ? ['Niebla', '🌫️'] : code <= 67 ? ['Lluvia', '🌧️'] : code <= 77 ? ['Nieve', '❄️'] : code <= 82 ? ['Chaparrones', '🌦️'] : ['Tormenta', '⛈️'];
    return { temp: w.temperature_2m, desc, icon, viento: w.wind_speed_10m };
  } catch (e) {
    return null;
  }
}
